import { Injectable, NgModuleFactoryLoader, Injector, Input, ViewContainerRef, SystemJsNgModuleLoader, Compiler, Inject, NgModuleFactory, ViewRef, ElementRef, ComponentRef } from '@angular/core';
import { NgxCmModalContext, NgxCmComponentInput, NgxCmComponentOutput, NgxCmModuleInfo } from '../models';
import { NGX_CM_MODULE_LOADER_REGISTRY } from '../_internals';
import { ComponentFactory, ComponentFactoryResolver } from '@angular/core';
import { NgxModalContainerComponent } from '../ngx-cm-components/ngx-modal-container/ngx-modal-container.component';
import { Observable } from 'rxjs';

@Injectable()
export class NgxComponentService {
  public container: ViewContainerRef;

  constructor(
    private _ngModuleFactoryLoader: NgModuleFactoryLoader,
    private _compiler: Compiler,
    private _injector: Injector,
    private _componentFactoryResolver: ComponentFactoryResolver,
    @Inject(NGX_CM_MODULE_LOADER_REGISTRY) private _moduleRegistry: NgxCmModuleInfo[]
  ) {
  }

  OpenModal(context: NgxCmModalContext): Observable<NgxCmModalContext> {
    return new Observable((observer) => {
      try {
        let moduleId = context.ComponentContext.ModuleId;
        this.BuildModal(context);

        this.LoadModule(moduleId).then(moduleRef => {
          let componentFactory = this.ComponentFactory(moduleRef, context.ComponentContext.Component);

          this.UpdateContainer(componentFactory, context, () => {
            moduleRef.destroy();
          });

          observer.next(context);
          observer.complete();
        }, reason => {
          console.error(reason);
          observer.error();
        });
      } catch (err) {
        console.error(err);
        observer.error();
      }
    })
  }

  LoadComponentFactory(context: NgxCmModalContext): Observable<any> {
    return new Observable(observer => {
      try {
        let moduleId = context.ComponentContext.ModuleId;
        this.LoadModule(moduleId).then(moduleRef => {
          let factory = this.ComponentFactory(moduleRef, context.ComponentContext.Component)
          observer.next(factory);
          observer.complete();
        }, reason => {
          console.error(reason);
          observer.error();
        });
      } catch (err) {
        console.error(err);
        observer.error();
      }
    })
  }

  async LoadModule(moduleId: string) {
    let moduleInfo = this.FindModule(moduleId);
    let moduleFactory = await this.GetModuleFactory(moduleInfo);
    let moduleRef = moduleFactory.create(this._injector);

    return moduleRef;
  }

  BuildModal(context: NgxCmModalContext) {
    let componentFactory = this._componentFactoryResolver.resolveComponentFactory(NgxModalContainerComponent);
    context.ComponentContainerRef = componentFactory.create(this._injector);
    this.container.insert(context.ComponentContainerRef.hostView);
  }

  UpdateContainer(hostComponentFactory: ComponentFactory<any>, context: NgxCmModalContext, cleanUp?: Function) {
    context.ComponentContainerRef.instance['ComponentFactory'] = hostComponentFactory;
    context.ComponentContainerRef.instance['ModalContext'] = context;
    let customSubscription;
    let closeSubscription;
    closeSubscription = context.ComponentContainerRef.instance['Close'].subscribe(value => {
      context.ComponentContext.TriggerClose(true);
    });
    customSubscription = context.ComponentContext.changes().subscribe(value => {
      if (value) {
        context.ComponentContainerRef.destroy();
        if (cleanUp) {
          cleanUp();
          try {
            if (closeSubscription) {
              closeSubscription.unsubscribe();
            }
            if (customSubscription) {
              customSubscription.unsubscribe();
            }
          } catch (err) { console.error(err); }
        }
      }
    })
  }

  FindModule(moduleId) {
    const moduleInfoList = this._moduleRegistry
      .filter(_moduleInfo => _moduleInfo.moduleId === moduleId);

    const moduleInfo = moduleInfoList[0];

    if (moduleInfo == null) {
      throw moduleNotFoundError(moduleId);
    }

    /* Counting for duplicates but only if they have a different location. */
    const duplicateCount = moduleInfoList
      .filter(_moduleInfo => _moduleInfo.loadChildren !== moduleInfo.loadChildren)
      .length;

    if (duplicateCount > 0) {
      throw duplicateModuleDeclarationError(moduleInfo.moduleId);
    }

    return moduleInfo;
  }

  ComponentFactory(moduleRef, component) {
    let componentFactoryResolver = moduleRef.componentFactoryResolver;
    let componentFactory = componentFactoryResolver.resolveComponentFactory(component);

    return componentFactory;
  }

  private GetModuleFactory(moduleRegistryItem: NgxCmModuleInfo): Promise<NgModuleFactory<any>> {
    return this._ngModuleFactoryLoader.load(moduleRegistryItem.loadChildren);
  }
}

export function componentNotFoundError(selector: string) {
  return new Error(`Component '<${selector}>' not found.`);
}

export function duplicateModuleDeclarationError(moduleId: string) {
  return new Error(`Module with id '${moduleId}' has been declared more than once with different locations.`);
}

export function moduleNotFoundError(moduleId: string) {
  return new Error(`Module with id '${moduleId}' not found.`);
}
